import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from 'vendor/material';
import { Checkbox, Link, TextField, Alert } from '@tackle-io/platform-ui';
import useStyles from 'pages/Settings/Account/marketplace/VendorConfig.styles';
import { Delete, Floppy } from 'mdi-material-ui';
import { ComponentProps, useState } from 'react';
import { gql } from '@apollo/client';
import {
  useCoSellSettingsMicrosoftSolutionsQuery,
  useCreateCoSellMicrosoftSolutionMutation,
  CoSellSettingsMicrosoftSolutionsQuery,
  useUpdateCoSellMicrosoftSolutionMutation,
  useDeleteCoSellMicrosoftSolutionMutation,
} from 'generated/graphql';

const CO_SELL_SETTINGS_MICROSOFT_SOLUTIONS_QUERY = gql`
  query CoSellSettingsMicrosoftSolutions {
    currentUser {
      id
      vendor {
        id
        coSell {
          id
          microsoftSolutions {
            id
            externalId
            solutionName
          }
        }
      }
    }
  }
`;

gql`
  mutation CreateCoSellMicrosoftSolution(
    $solution: CoSellMicrosoftSolutionCreateInput!
  ) {
    createCoSellMicrosoftSolution(solution: $solution) {
      id
      externalId
      solutionName
    }
  }
`;

gql`
  mutation UpdateCoSellMicrosoftSolution(
    $updates: CoSellMicrosoftSolutionUpdateInput!
  ) {
    updateCoSellMicrosoftSolution(updates: $updates) {
      id
      externalId
      solutionName
    }
  }
`;

gql`
  mutation DeleteCoSellMicrosoftSolution(
    $solution: CoSellMicrosoftSolutionDeleteInput!
  ) {
    deleteCoSellMicrosoftSolution(solution: $solution) {
      id
      externalId
      solutionName
      archivedAt
    }
  }
`;

const StyledIconButton = (props: ComponentProps<typeof IconButton>) => {
  const classes = useStyles();
  return <IconButton className={classes.iconButton} {...props} />;
};

const Solution = ({
  solution,
  vendorId,
}: {
  solution: CoSellSettingsMicrosoftSolutionsQuery['currentUser']['vendor']['coSell']['microsoftSolutions'][number];
  vendorId: string;
}) => {
  const [newSolutionName, setNewSolutionName] = useState(
    solution.solutionName ?? '',
  );
  const [updateSolution, { loading: updating, error: updateError }] =
    useUpdateCoSellMicrosoftSolutionMutation();
  const [deleteSolution, { loading: deleting, error: deleteError }] =
    useDeleteCoSellMicrosoftSolutionMutation({
      refetchQueries: [CO_SELL_SETTINGS_MICROSOFT_SOLUTIONS_QUERY],
    });
  const loading = updating || deleting;
  const hasError = Boolean(updateError || deleteError);
  return (
    <Grid container direction="row" spacing={2} alignItems="flex-end">
      <Grid item xs>
        <TextField
          id={`solution-name-${solution.id}`}
          name="Solution name"
          label="Solution name"
          value={newSolutionName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setNewSolutionName(e.target.value)
          }
          disabled={loading}
          error={hasError && 'Something went wrong'}
        />
      </Grid>
      <Grid item xs>
        <TextField
          name="Solution ID"
          id={`solution-id-${solution.id}`}
          label="Solution ID"
          disabled
          value={solution.externalId}
        />
      </Grid>
      <Grid item>
        {loading ? (
          <StyledIconButton>
            <CircularProgress size={24} />
          </StyledIconButton>
        ) : solution.solutionName === newSolutionName ? (
          <StyledIconButton
            onClick={() =>
              deleteSolution({
                variables: {
                  solution: {
                    id: solution.id,
                    vendorId,
                  },
                },
              })
            }
            disabled={loading}
          >
            <Delete />
          </StyledIconButton>
        ) : (
          <StyledIconButton
            onClick={() =>
              updateSolution({
                variables: {
                  updates: {
                    id: solution.id,
                    vendorId: vendorId,
                    solutionName: newSolutionName,
                  },
                },
              })
            }
            disabled={loading}
          >
            <Floppy />
          </StyledIconButton>
        )}
      </Grid>
    </Grid>
  );
};

const CoSellSettingsMicrosoftSolutions = () => {
  const classes = useStyles();
  const [newSolutionName, setNewSolutionName] = useState('');
  const [newSolutionId, setNewSolutionId] = useState('');
  const { data, loading, error } = useCoSellSettingsMicrosoftSolutionsQuery({
    notifyOnNetworkStatusChange: true,
  });
  const [
    createNewSolution,
    { loading: creatingNewSolution, error: createSolutionError },
  ] = useCreateCoSellMicrosoftSolutionMutation({
    refetchQueries: [CO_SELL_SETTINGS_MICROSOFT_SOLUTIONS_QUERY],
    onCompleted: () => {
      setNewSolutionName('');
      setNewSolutionId('');
    },
  });
  return (
    <Grid container direction="column">
      <Grid item>
        <Checkbox
          className={classes.checkBox}
          label={
            <Typography>
              <b>Product Details</b>
            </Typography>
          }
          checked={
            data?.currentUser?.vendor?.coSell?.microsoftSolutions?.length > 0
          }
        />
      </Grid>
      <Grid item className={classes.textGrid}>
        <Box pl={4}>
          <Typography>
            List out the products that you will be co-selling with Microsoft to
            pre-populate the submission form when creating a co-sell later on.
            Go to your product listings in the{' '}
            <Link
              showExternalIcon
              external
              to="https://partner.microsoft.com/en-US/dashboard/referrals/cosell/solution/publish"
              className={classes.link}
            >
              Partner Center
            </Link>
            . Enter the information below:
          </Typography>
        </Box>
      </Grid>
      {error && (
        <Alert
          appearance="danger"
          title="Something went wrong, unable to load data"
        />
      )}
      {data?.currentUser?.vendor?.coSell?.microsoftSolutions?.map(
        (solution) => (
          <Grid item key={solution.id}>
            <Box pl={4}>
              <Solution
                solution={solution}
                vendorId={data.currentUser.vendor.id}
              />
            </Box>
          </Grid>
        ),
      )}
      {loading || creatingNewSolution ? (
        <Grid item>
          <Box pl={4}>
            <Grid container direction="row" spacing={2} alignItems="flex-end">
              <Grid item xs>
                <TextField
                  id="solution-name-loading"
                  name="solution-name-loading"
                  disabled
                  label="Solution name"
                  value=""
                />
              </Grid>
              <Grid item xs>
                <TextField
                  id="solution-id-loading"
                  name="solution-id-loading"
                  disabled
                  label="Solution ID"
                  value=""
                />
              </Grid>
              <Grid item>
                <StyledIconButton>
                  <CircularProgress size={24} />
                </StyledIconButton>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      ) : (
        <Grid item>
          <Box pl={4}>
            <Grid container direction="row" spacing={2} alignItems="flex-end">
              <Grid item xs>
                <TextField
                  name="Solution Name"
                  id="solution-name-new"
                  disabled={creatingNewSolution}
                  label="Solution name"
                  value={newSolutionName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setNewSolutionName(e.target.value)
                  }
                  error={createSolutionError && 'Something went wrong'}
                />
              </Grid>
              <Grid item xs>
                <TextField
                  name="Solution ID"
                  id="solution-id-new"
                  disabled={creatingNewSolution}
                  label="Solution ID"
                  value={newSolutionId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setNewSolutionId(e.target.value)
                  }
                />
              </Grid>
              <Grid item>
                {creatingNewSolution ? (
                  <CircularProgress size={24} />
                ) : (
                  <StyledIconButton
                    disabled={!Boolean(newSolutionName && newSolutionId)}
                    onClick={() =>
                      createNewSolution({
                        variables: {
                          solution: {
                            externalId: newSolutionId,
                            solutionName: newSolutionName,
                            vendorId: data.currentUser.vendor.id,
                          },
                        },
                      })
                    }
                  >
                    <Floppy />
                  </StyledIconButton>
                )}
              </Grid>
            </Grid>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default CoSellSettingsMicrosoftSolutions;
